import { fetchWithAuth } from './fetchWithAuth';
import { API_BASE_URL } from '../config';

// ── Billing Groups ────────────────────────────────────────────────────────────

export async function apiFetchBillingGroups(tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-groups?tenant_id=${tenantId}`);
    if (!res.ok) throw new Error('Failed to fetch billing groups');
    return res.json();
}

export async function apiCreateBillingGroup(tenantId, { name, description, sort_order }) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-groups`, {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId, name, description, sort_order }),
    });
    if (!res.ok) throw new Error('Failed to create billing group');
    return res.json();
}

export async function apiUpdateBillingGroup(id, fields) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-groups/${id}`, {
        method: 'PUT',
        body: JSON.stringify(fields),
    });
    if (!res.ok) throw new Error('Failed to update billing group');
    return res.json();
}

export async function apiDeleteBillingGroup(id, tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-groups/${id}?tenant_id=${tenantId}`, {
        method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete billing group');
}

// ── GL Accounts ───────────────────────────────────────────────────────────────

export async function apiFetchGLAccounts(tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/gl-accounts?tenant_id=${tenantId}`);
    if (!res.ok) throw new Error('Failed to fetch GL accounts');
    return res.json();
}

export async function apiCreateGLAccount(tenantId, { billing_group_id, code, name, sort_order }) {
    const res = await fetchWithAuth(`${API_BASE_URL}/gl-accounts`, {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId, billing_group_id, code, name, sort_order }),
    });
    if (!res.ok) throw new Error('Failed to create GL account');
    return res.json();
}

export async function apiUpdateGLAccount(id, fields) {
    const res = await fetchWithAuth(`${API_BASE_URL}/gl-accounts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(fields),
    });
    if (!res.ok) throw new Error('Failed to update GL account');
    return res.json();
}

export async function apiDeleteGLAccount(id, tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/gl-accounts/${id}?tenant_id=${tenantId}`, {
        method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete GL account');
}

// ── Billing Sheets ────────────────────────────────────────────────────────────

export async function apiFetchBillingSheets(tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-sheets?tenant_id=${tenantId}`);
    if (!res.ok) throw new Error('Failed to fetch billing sheets');
    return res.json();
}

export async function apiFetchBillingSheet(id, tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-sheets/${id}?tenant_id=${tenantId}`);
    if (!res.ok) throw new Error('Failed to fetch billing sheet');
    return res.json();
}

export async function apiCreateBillingSheet(tenantId, payload) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-sheets`, {
        method: 'POST',
        body: JSON.stringify({ tenant_id: tenantId, ...payload }),
    });
    if (!res.ok) throw new Error('Failed to create billing sheet');
    return res.json();
}

export async function apiUpdateBillingSheet(id, payload) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-sheets/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to update billing sheet');
    return res.json();
}

export async function apiDeleteBillingSheet(id, tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-sheets/${id}?tenant_id=${tenantId}`, {
        method: 'DELETE',
    });
    if (!res.ok) throw new Error('Failed to delete billing sheet');
}

// ── Sheet-scoped GL assignments ────────────────────────────────────────────────

export async function apiFetchSheetGLAssignments(id, tenantId) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-sheets/${id}/gl-assignments?tenant_id=${tenantId}`);
    if (!res.ok) throw new Error('Failed to fetch GL assignments');
    return res.json();
}

/** assignments: [{ billing_group_id, gl_account_id, sort_order }] */
export async function apiUpdateSheetGLAssignments(id, tenantId, assignments) {
    const res = await fetchWithAuth(`${API_BASE_URL}/billing-sheets/${id}/gl-assignments`, {
        method: 'PUT',
        body: JSON.stringify({ tenant_id: tenantId, assignments }),
    });
    if (!res.ok) throw new Error('Failed to update GL assignments');
    return res.json();
}

/**
 * Resolve which sheet config to use.
 * Always returns a named sheet when any exist — legacy "all" / missing ids
 * fall back to the default sheet, then the first sheet.
 */
export function resolveBillingSheet(sheets, sheetId) {
    if (!sheets?.length) return null;
    if (sheetId && sheetId !== 'default' && sheetId !== 'all') {
        const found = sheets.find(s => String(s.id) === String(sheetId));
        if (found) return found;
    }
    return sheets.find(s => s.is_default) || sheets[0] || null;
}
